import startCase from "lodash/startCase";

export const SENTINEL_LABELS: Record<string, string> = {
  notDisclosed: "Not Disclosed",
  notIncluded: "Not Included",
  notOffered: "Not Offered",
  notApplicable: "Not Applicable",
};

export const isSentinel = (value: unknown): value is string =>
  typeof value === "string" && value in SENTINEL_LABELS;

export const sentinelLabel = (value: string): string =>
  SENTINEL_LABELS[value] ?? "";

const RATE_GUARANTEE_TYPES_WITHOUT_CAP = new Set(["notOffered", "notIncluded"]);

export const rateGuaranteeHasNoCap = (type: unknown): boolean =>
  typeof type === "string" && RATE_GUARANTEE_TYPES_WITHOUT_CAP.has(type);

const currency0 = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatCurrency = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return currency0.format(num);
};

export const formatPercent = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return `${num}%`;
};

export const formatMultiplier = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "";
  return `${num}x salary`;
};

/** Coerce a value to a finite number, treating null/undefined/blank as absent. */
const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Pull the numeric values out of a `{amount}[]` list (or a bare number). Amounts
 * are coerced with `Number(...)`, so string-encoded values like `{ amount: "10000" }`
 * are kept.
 */
const extractAmountList = (raw: unknown): number[] => {
  const entries = Array.isArray(raw) ? raw : [raw];
  return entries
    .map((entry) =>
      entry && typeof entry === "object" && "amount" in entry
        ? toFiniteNumber((entry as { amount: unknown }).amount)
        : toFiniteNumber(entry),
    )
    .filter((value): value is number => value !== null);
};

/** A multi-value dollar benefit: "$10,000, $25,000, $50,000". */
export const formatMultipleDollarAmounts = (raw: unknown): string =>
  extractAmountList(raw)
    .map((amount) => formatCurrency(amount))
    .filter(Boolean)
    .join(", ");

/**
 * A multi-value salary-multiplier benefit: "1x, 2x, or 3x salary". Two values
 * join with a bare "or" ("1x or 2x salary"); three or more take an Oxford "or".
 */
export const formatMultipleSalaryMultipliers = (raw: unknown): string => {
  const tokens = extractAmountList(raw).map((value) => `${value}x`);
  if (tokens.length === 0) return "";
  if (tokens.length === 1) return `${tokens[0]} salary`;

  const last = tokens[tokens.length - 1];
  const leading = tokens.slice(0, -1);
  const joined =
    tokens.length === 2
      ? `${leading[0]} or ${last}`
      : `${leading.join(", ")}, or ${last}`;

  return `${joined} salary`;
};

/**
 * Benefit Amount — flatAmount → $X; percentageOfSalary → X%;
 * increments → "$X increments". Shared by STD and LTD, whose benefit-amount
 * type enums are identical.
 */
export const formatBenefitAmount = (row: Record<string, unknown>): string => {
  const type = row["benefitAmountType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (type === "flatAmount")
    return formatCurrency(row["benefitAmountAmount"]) || "";
  if (type === "percentageOfSalary")
    return formatPercent(row["benefitAmountAmount"]) || "";
  if (type === "increments") {
    const amount = formatCurrency(row["benefitAmountAmount"]);
    return amount ? `${amount} increments` : "";
  }
  return "";
};

export const enumLabel =
  (fieldKey: string, labels: Record<string, string>) =>
  (row: Record<string, unknown>): string => {
    const value = row[fieldKey];
    if (typeof value !== "string" || value === "") return "";
    if (isSentinel(value)) return sentinelLabel(value);
    return labels[value] ?? "";
  };

const NOT_INCLUDED = "Not Included";

/** What a cell shows when the plan holds no answer for the field. */
const NO_DATA = "-";

export const INCLUSION_LABELS: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED,
};

export const PORTABILITY_LABELS: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED,
  includedWithEOI: "Included with EOI",
  includedWithoutEOI: "Included without EOI",
};

/**
 * Eligibility Hours dropdown values (CD-2325), which replaced free text on
 * every LOC carrying the row. Mirrors plan-grid-core's `eligibilityHoursLabels`
 * so the export reads a cell the way the grid shows it; that list's
 * `notIncluded` / `notDisclosed` are sentinels here and carry the same labels
 * from SENTINEL_LABELS.
 */
export const ELIGIBILITY_HOURS_LABELS: Record<string, string> = {
  noMinimum: "No Minimum",
  hours20: "20 hrs/week",
  hours25: "25 hrs/week",
  hours30: "30 hrs/week",
  hours35: "35 hrs/week",
  hours37Point5: "37.5 hrs/week",
  hours40: "40 hrs/week",
};

/**
 * Eligibility Hours, carried on `benefits.extended` by every LOC that has the
 * row. A dropdown since CD-2325, so the stored value is an enum key; plans
 * quoted before that still hold whatever an analyst typed, which falls through
 * as-is rather than being blanked by a plain `enumLabel` — a blank value makes
 * `fieldValues` drop the row, so legacy data would vanish from the sheet.
 */
export const formatEligibilityHours = (
  row: Record<string, unknown>,
): string => {
  const value = row["eligibilityHours"];
  if (typeof value !== "string" || value === "") return "";
  if (isSentinel(value)) return sentinelLabel(value);
  return ELIGIBILITY_HOURS_LABELS[value] ?? value;
};

/**
 * `enumLabel` for a dropdown whose cell accepts typing, so the stored value is
 * not always one of the offered keys — the AD&D riders and the EAP visit limit
 * both hold analyst-typed values in real data. The grid title-cases whatever it
 * finds instead of blanking the cell, and a blank here would make `fieldValues`
 * drop the row, so the sheet follows it (CD-2676).
 *
 * The fallback calls the same `startCase` plan-grid-core's `formatEnumValue`
 * does, rather than approximating it: its word splitting has rules a hand-rolled
 * version gets wrong (`"8888bbbb"` → `"8888 Bbbb"`, `"45thUcr"` → `"45th Ucr"`),
 * and a near-miss here is a cell that reads differently in the export than on
 * the tab it is supposed to mirror.
 */
export const enumLabelOrTitleCase =
  (fieldKey: string, labels: Record<string, string>) =>
  (row: Record<string, unknown>): string => {
    const value = row[fieldKey];
    if (typeof value !== "string" || value === "") return "";
    if (isSentinel(value)) return sentinelLabel(value);
    return labels[value] ?? startCase(value);
  };

/**
 * EAP face-to-face visit limit types. Mirrors plan-grid-core's
 * `eapFaceToFaceVisitsLimitTypeLabels` so the sheet reads the cell the way the
 * grid shows it.
 */
export const EAP_FACE_TO_FACE_VISITS_LIMIT_TYPE_LABELS: Record<string, string> =
  {
    perIssue: "Per Issue",
    perYear: "Per Year",
    perLifetime: "Per Lifetime",
    unlimited: "Unlimited",
    notIncluded: NOT_INCLUDED,
  };

export const ANNUAL_OPEN_ENROLLMENT_LABELS: Record<string, string> = {
  oneTimeOpenEnrollment: "One Time Open Enrollment",
  annualOpenEnrollment: "Annual Open Enrollment",
  modifiedOpenEnrollment: "Modified Open Enrollment",
  included: "Included",
  notIncluded: NOT_INCLUDED,
};

export const EMPLOYER_CONTRIBUTION_LABELS: Record<string, string> = {
  oneHundredPercentEmployerPaid: "100% Employer Paid",
  contributory: "Contributory",
  voluntary: "Voluntary",
  bonusUp: "Bonus Up",
  grossUp: "Gross Up",
  taxChoice: "Tax Choice",
  notIncluded: NOT_INCLUDED,
};

interface AgeReductionBand {
  reductionPercent: number;
  age: number;
}

// The schedule sibling arrives as an array of `{reductionPercent, age}` bands
// or, from some persisted shapes, a JSON-encoded string. Coerce either.
const parseAgeReductionBands = (raw: unknown): AgeReductionBand[] => {
  let list: unknown = raw;

  if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(list)) return [];

  return list
    .map((entry) => {
      const { reductionPercent, age } = (entry ?? {}) as {
        reductionPercent?: unknown;
        age?: unknown;
      };

      return { reductionPercent: Number(reductionPercent), age: Number(age) };
    })
    .filter(
      (band) =>
        Number.isFinite(band.reductionPercent) && Number.isFinite(band.age),
    );
};

/**
 * Age Reduction Schedule formatter factory. Renders one age band per line
 * ("50% at 65\n75% at 70") so each band reads separately in the Quotes grid
 * and in the marketing-presentation workbook, both of which wrap cell text.
 * Sentinels map to their label; an empty schedule renders as "".
 */
export const buildAgeReductionScheduleFormatter =
  (typeKey: string, detailKey: string) =>
  (row: Record<string, unknown>): string => {
    const type = row[typeKey];
    if (isSentinel(type)) return sentinelLabel(type);

    // Only the schedule-based type renders bands; other types (e.g. `none`)
    // show nothing even if a stale/default schedule is present.
    if (type !== "percentageAtAge") return "";

    const bands = parseAgeReductionBands(row[detailKey]);
    return bands
      .map((band) => `${band.reductionPercent}% at ${band.age}`)
      .join("\n");
  };

/**
 * Age Reduction Schedule formatter shared by Basic Life, Critical Illness,
 * and Accident — all of which key off the generic `ageReductionScheduleType`/
 * `ageReductionScheduleSchedules` pair (Voluntary Life has two independent
 * fields instead — see `ageReductionsEe`/`ageReductionsSp` in volLife.ts).
 */
export const formatAgeReductionSchedule = buildAgeReductionScheduleFormatter(
  "ageReductionScheduleType",
  "ageReductionScheduleSchedules",
);

export const SERVICE_WAITING_PERIOD_LABELS: Record<string, string> = {
  preventive: "Preventive",
  basic: "Basic",
  major: "Major",
  ortho: "Ortho",
  sealants: "Sealants",
  spaceMaintainers: "Space Maintainers",
  generalAnesthesia: "General Anesthesia",
  compositeWhiteFillings: "Composite (White) Fillings",
  endodontics: "Endodontics",
  periodonticsSurgical: "Periodontics (Surgical)",
  periodonticsNonSurgical: "Periodontics (Non-Surgical)",
  oralSurgerySimple: "Oral Surgery (Simple)",
  oralSurgeryComplex: "Oral Surgery (Complex)",
  implants: "Implants",
};

interface ServiceWaitingPeriodEntry {
  type: string;
  months: number;
}

const parseServiceWaitingPeriods = (
  raw: unknown,
): ServiceWaitingPeriodEntry[] => {
  if (!Array.isArray(raw)) return [];

  return raw
    .map((entry) => {
      const { type, months } = (entry ?? {}) as {
        type?: unknown;
        months?: unknown;
      };

      return { type: String(type), months: Number(months) };
    })
    .filter(
      (entry) =>
        entry.type in SERVICE_WAITING_PERIOD_LABELS &&
        Number.isFinite(entry.months),
    );
};

/**
 * Service Waiting Periods formatter for Dental PPO. The field holds a
 * `{type, months}[]` array; each entry renders as
 * "[Type label] - [months] month(s)", one per line.
 *
 * An empty array is the "None" sentinel and renders as "None". Anything that is
 * not an array means the plan holds no answer to report — an awaiting-quote
 * placeholder, or a value that came back null — and renders as no-data, because
 * "None" would claim the plan has no waiting periods.
 */
export const formatServiceWaitingPeriods = (
  row: Record<string, unknown>,
): string => {
  const raw = row["serviceWaitingPeriods"];
  if (!Array.isArray(raw)) return NO_DATA;

  const periods = parseServiceWaitingPeriods(raw);
  if (periods.length === 0) return "None";

  return periods
    .map(
      (period) =>
        `${SERVICE_WAITING_PERIOD_LABELS[period.type]} - ${period.months} month(s)`,
    )
    .join("\n");
};

export const DEFINITION_OF_EARNINGS_LABELS: Record<string, string> = {
  salary: "Base Salary",
  salaryPlusCommissions: "Salary + Comm",
  salaryPlusBonus: "Salary + Bonus",
  salaryPlusBonusPlusCommissions: "Salary + Bonus + Comm",
  priorYearW2: "Prior Year W2",
  k1: "K1",
  notApplicable: "Not Applicable",
  notIncluded: NOT_INCLUDED,
};

/** Definition of Earnings types whose label takes an "(N mo avg)" suffix. */
const DEFINITION_OF_EARNINGS_AVERAGE_TYPES = new Set([
  "salaryPlusCommissions",
  "salaryPlusBonus",
  "salaryPlusBonusPlusCommissions",
]);

/**
 * Definition of Earnings — type label, with the averaging window appended for
 * the salary-plus-X types when the average months sibling is a positive
 * number (e.g. "Salary + Comm (12 mo avg)"). Shared by every LOC that carries
 * the field (STD, LTD, Basic Life, Voluntary Life).
 */
export const formatDefinitionOfEarnings = (
  row: Record<string, unknown>,
): string => {
  const type = row["definitionOfEarningsType"];
  if (isSentinel(type)) return sentinelLabel(type);
  if (typeof type !== "string") return "";

  const label = DEFINITION_OF_EARNINGS_LABELS[type] ?? "";
  if (!label || !DEFINITION_OF_EARNINGS_AVERAGE_TYPES.has(type)) return label;

  const months = Number(row["definitionOfEarningsAverageMonths"]);
  return Number.isFinite(months) && months > 0
    ? `${label} (${months} mo avg)`
    : label;
};

export const DEFINITION_OF_DISABILITY_LABELS: Record<string, string> = {
  lossOfDuties: "Loss of Duties",
  lossOfEarnings: "Loss of Earnings",
  lossOfEarningsAndDuties: "Loss of Duties AND Loss of Earnings",
  lossOfEarningsOrLossOfDuties: "Loss of Duties OR Loss of Earnings",
};

export const FRAMES_DISCOUNTS_RULE_TYPE = {
  percentage: "upToPercentageOff",
  dollar: "upToDollarAmountOff",
};

// The labels the grid's type dropdown shows (startCase of the enum value). A type
// saved without an amount prints its label, so the choice still reaches exports
// and read-only views instead of disappearing.
const MONTHS_OR_YEARS_TYPE_LABELS: Record<string, string> = {
  months: "Months",
  years: "Years",
};

/**
 * "N month(s)" / "N year(s)" for a months/years type-amount pair, the bare type
 * label ("Months") when the amount is missing, or "".
 */
export const formatMonthsOrYears = (type: unknown, amount: unknown): string => {
  if (amount == null)
    return typeof type === "string"
      ? (MONTHS_OR_YEARS_TYPE_LABELS[type] ?? "")
      : "";
  if (type === "months")
    return `${String(amount)} ${amount === 1 ? "month" : "months"}`;
  if (type === "years")
    return `${String(amount)} ${amount === 1 ? "year" : "years"}`;
  return "";
};

/**
 * Pre-existing condition months are held for editing as a positional,
 * zero-padded `before/treatment[/after]` string, so a quote stating only
 * "3/12" — diagnosis-free months before and after the effective date, with no
 * treatment-free clause — is stored as "03/00/12". Display strips the padding
 * and the empty treatment placeholder so the cell reads as the carrier stated
 * it. A detail string whose parts are not all whole months is shown unchanged —
 * an analyst's free-text value is surfaced as typed rather than reinterpreted.
 */
export const formatPreExistingConditionMonthsDetail = (
  detail: string,
): string => {
  // Number (not parseInt) so a partially-numeric part like "12a" is rejected
  // rather than silently truncated to 12; blank parts are not months either.
  const months = detail
    .split("/")
    .map((part) => (part.trim() === "" ? NaN : Number(part)));
  if (months.some((month) => !Number.isInteger(month))) return detail;

  const [before, treatment, ...rest] = months;
  const shown = treatment === 0 ? [before, ...rest] : months;
  return shown.join("/");
};

/** Rate Guarantee — months/years expressed with the amount, or a sentinel. */
export const formatRateGuarantee = (row: Record<string, unknown>): string => {
  const type = row["rateGuaranteeType"];
  if (isSentinel(type)) return sentinelLabel(type as string);

  return formatMonthsOrYears(type, row["rateGuaranteeAmount"]);
};
